import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type MediaOwnerRole = 'owner' | 'manager' | 'viewer' | 'custom';

export interface CustomPermissions {
  canEdit: boolean;
  canAddLocation: boolean;
  canDeleteLocation: boolean;
  canApprove: boolean;
  canReject: boolean;
  canViewRevenue: boolean;
  canViewAnalytics: boolean;
  canManageTeam: boolean;
  canUpload: boolean;
  canDeleteDocument: boolean;
  canViewActivity: boolean;
  canSubmitSupport: boolean;
}

export interface MediaOwnerPermissions {
  role: MediaOwnerRole;
  isPrimary: boolean;
  displayName: string | null;
  canEdit: boolean;
  canAddLocation: boolean;
  canDeleteLocation: boolean;
  canApprove: boolean;
  canReject: boolean;
  canViewRevenue: boolean;
  canViewAnalytics: boolean;
  canManageTeam: boolean;
  canUpload: boolean;
  canDeleteDocument: boolean;
  canViewActivity: boolean;
  canSubmitSupport: boolean;
  customPermissions: CustomPermissions | null;
  loading: boolean;
}

export const useMediaOwnerRole = (mediaOwnerName: string | null): MediaOwnerPermissions => {
  const { user } = useAuth();
  const [role, setRole] = useState<MediaOwnerRole>('viewer');
  const [isPrimary, setIsPrimary] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [customPermissions, setCustomPermissions] = useState<CustomPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email || !mediaOwnerName) {
      setLoading(false);
      return;
    }

    const fetchRole = async () => {
      const { data } = await supabase
        .from('media_owner_users')
        .select('role, is_primary, custom_permissions, display_name')
        .eq('email', user.email!)
        .eq('media_owner_name', mediaOwnerName)
        .eq('is_active', true)
        .maybeSingle();

      if (data) {
        const r = data.role as MediaOwnerRole;
        setRole(r);
        setIsPrimary(data.is_primary);
        setDisplayName(data.display_name || null);
        if (r === 'custom' && data.custom_permissions) {
          const cp = data.custom_permissions as Record<string, boolean>;
          setCustomPermissions({
            canEdit: !!cp.canEdit,
            canAddLocation: !!cp.canAddLocation,
            canDeleteLocation: !!cp.canDeleteLocation,
            canApprove: !!cp.canApprove,
            canReject: !!cp.canReject,
            canViewRevenue: !!cp.canViewRevenue,
            canViewAnalytics: !!cp.canViewAnalytics,
            canManageTeam: !!cp.canManageTeam,
            canUpload: !!cp.canUpload,
            canDeleteDocument: !!cp.canDeleteDocument,
            canViewActivity: !!cp.canViewActivity,
            canSubmitSupport: !!cp.canSubmitSupport,
          });
        } else {
          setCustomPermissions(null);
        }
      } else {
        setRole('owner');
        setIsPrimary(true);
        setCustomPermissions(null);
      }
      setLoading(false);
    };

    fetchRole();
  }, [user?.email, mediaOwnerName]);

  // Derive permissions from role
  let canEdit: boolean;
  let canAddLocation: boolean;
  let canDeleteLocation: boolean;
  let canApprove: boolean;
  let canReject: boolean;
  let canViewRevenue: boolean;
  let canViewAnalytics: boolean;
  let canManageTeam: boolean;
  let canUpload: boolean;
  let canDeleteDocument: boolean;
  let canViewActivity: boolean;
  let canSubmitSupport: boolean;

  if (role === 'owner') {
    canEdit = true; canAddLocation = true; canDeleteLocation = true;
    canApprove = true; canReject = true;
    canViewRevenue = true; canViewAnalytics = true;
    canManageTeam = true; canUpload = true; canDeleteDocument = true;
    canViewActivity = true; canSubmitSupport = true;
  } else if (role === 'manager') {
    canEdit = true; canAddLocation = true; canDeleteLocation = false;
    canApprove = true; canReject = true;
    canViewRevenue = true; canViewAnalytics = true;
    canManageTeam = false; canUpload = true; canDeleteDocument = true;
    canViewActivity = true; canSubmitSupport = true;
  } else if (role === 'custom' && customPermissions) {
    canEdit = customPermissions.canEdit;
    canAddLocation = customPermissions.canAddLocation;
    canDeleteLocation = customPermissions.canDeleteLocation;
    canApprove = customPermissions.canApprove;
    canReject = customPermissions.canReject;
    canViewRevenue = customPermissions.canViewRevenue;
    canViewAnalytics = customPermissions.canViewAnalytics;
    canManageTeam = customPermissions.canManageTeam;
    canUpload = customPermissions.canUpload;
    canDeleteDocument = customPermissions.canDeleteDocument;
    canViewActivity = customPermissions.canViewActivity;
    canSubmitSupport = customPermissions.canSubmitSupport;
  } else {
    // viewer or custom without permissions
    canEdit = false; canAddLocation = false; canDeleteLocation = false;
    canApprove = false; canReject = false;
    canViewRevenue = false; canViewAnalytics = false;
    canManageTeam = false; canUpload = false; canDeleteDocument = false;
    canViewActivity = false; canSubmitSupport = false;
  }

  return {
    role, isPrimary, displayName,
    canEdit, canAddLocation, canDeleteLocation,
    canApprove, canReject,
    canViewRevenue, canViewAnalytics,
    canManageTeam, canUpload, canDeleteDocument,
    canViewActivity, canSubmitSupport,
    customPermissions, loading,
  };
};
